// ESPACE V2 — Forum · Covoiturage · Galerie · Disponibilités
import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useMember, formatErr } from "@/contexts/MemberAuthContext";

const HERO = "/photos/prieure-leaves.jpg";

function Shell({ title, kicker, children }) {
  return (
    <div data-testid="espace-v2-page">
      <section className="relative" style={{ height: "32vh", minHeight: 220 }}>
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, rgba(20,16,12,0.55), rgba(20,16,12,0.4))" }} />
        <div className="container-prieure relative h-full flex flex-col justify-end pb-8">
          <Link to="/espace" className="font-caps text-[0.65rem] text-white/80" style={{ letterSpacing: "0.25em" }}>← MON ESPACE</Link>
          {kicker && <div className="font-caps text-[0.7rem] mt-3" style={{ color: "var(--gold)", letterSpacing: "0.25em" }}>{kicker}</div>}
          <h1 className="font-display text-4xl sm:text-5xl text-white mt-2">{title}</h1>
        </div>
      </section>
      <div className="py-16">{children}</div>
    </div>
  );
}

function Guard({ children, allow }) {
  const { member } = useMember();
  if (member === null) return <Shell title="…"><p className="text-center">Chargement…</p></Shell>;
  if (!member) return <Navigate to="/espace/connexion" replace />;
  if (allow && !allow.includes(member.role)) return <Navigate to="/espace" replace />;
  return children;
}

// =================================================== FORUM
export function EspaceForum() {
  const { espace } = useMember();
  const [topics, setTopics] = useState([]);
  const [form, setForm] = useState({ title: "", body: "" });
  const [err, setErr] = useState("");
  const reload = () => espace.get("/espace/forum/topics").then(r => setTopics(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await espace.post("/espace/forum/topics", form); setForm({ title: "", body: "" }); reload(); }
    catch (ex) { setErr(formatErr(ex)); }
  };
  return (
    <Guard>
      <Shell title="Forum" kicker="ÉCHANGES ENTRE MEMBRES">
        <div className="container-prieure max-w-3xl space-y-8">
          <form onSubmit={submit} data-testid="forum-new" className="bg-white border p-6 space-y-3" style={{ borderColor: "var(--border)" }}>
            <h3 className="font-display text-xl">Lancer une discussion</h3>
            <input required placeholder="Titre" value={form.title} onChange={(e)=>setForm(s=>({...s,title:e.target.value}))} className="input-classic" />
            <textarea required rows={3} placeholder="Votre message…" value={form.body} onChange={(e)=>setForm(s=>({...s,body:e.target.value}))} className="input-classic" />
            {err && <p className="text-sm" style={{ color: "var(--cinnabar)" }}>{err}</p>}
            <button className="btn btn-primary">Publier</button>
          </form>
          <div className="space-y-3" data-testid="forum-list">
            {topics.length === 0 && <p className="text-center" style={{ color: "var(--muted)" }}>Aucun sujet pour l'instant.</p>}
            {topics.map(t => (
              <Link key={t.id} to={`/espace/forum/${t.id}`} data-testid={`topic-${t.id}`}
                    className="block bg-white border p-5 hover:shadow-md transition" style={{ borderColor: "var(--border)" }}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h4 className="font-display text-lg">{t.title}</h4>
                    <p className="text-sm mt-1 line-clamp-2" style={{ color: "var(--ink-2)" }}>{t.body}</p>
                  </div>
                  <span className="text-xs whitespace-nowrap" style={{ color: "var(--muted)" }}>
                    {(t.replies || []).length} rép.
                  </span>
                </div>
                <div className="text-xs mt-3" style={{ color: "var(--muted)" }}>
                  {t.author_name} · {new Date(t.created_at).toLocaleDateString("fr-FR")}
                </div>
              </Link>
            ))}
          </div>
        </div>
      </Shell>
    </Guard>
  );
}

export function EspaceForumTopic() {
  const { id } = useParams();
  const { espace } = useMember();
  const [topic, setTopic] = useState(null);
  const [reply, setReply] = useState("");
  const reload = () => espace.get(`/espace/forum/topics/${id}`).then(r => setTopic(r.data)).catch(() => setTopic(false));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [id]);
  const submit = async (e) => {
    e.preventDefault();
    if (!reply.trim()) return;
    await espace.post(`/espace/forum/topics/${id}/reply`, { body: reply });
    setReply(""); reload();
  };
  return (
    <Guard>
      <Shell title={topic?.title || "Sujet"} kicker="DISCUSSION">
        <div className="container-prieure max-w-3xl">
          {topic === false && <p>Sujet introuvable.</p>}
          {topic && (
            <>
              <article className="bg-white border p-6 mb-6" style={{ borderColor: "var(--border)" }}>
                <p style={{ whiteSpace: "pre-wrap" }}>{topic.body}</p>
                <div className="text-xs mt-4" style={{ color: "var(--muted)" }}>
                  {topic.author_name} · {new Date(topic.created_at).toLocaleString("fr-FR")}
                </div>
              </article>
              <div className="space-y-3 mb-6">
                {(topic.replies || []).map(r => (
                  <article key={r.id} className="bg-[var(--parchment-2)] border-l-2 pl-4 py-3" style={{ borderColor: "var(--gold)" }}>
                    <p style={{ whiteSpace: "pre-wrap" }}>{r.body}</p>
                    <div className="text-xs mt-2" style={{ color: "var(--muted)" }}>{r.author_name} · {new Date(r.created_at).toLocaleString("fr-FR")}</div>
                  </article>
                ))}
              </div>
              <form onSubmit={submit} className="bg-white border p-4 space-y-2" style={{ borderColor: "var(--border)" }}>
                <textarea required rows={3} placeholder="Votre réponse…" value={reply} onChange={(e)=>setReply(e.target.value)} className="input-classic" />
                <button className="btn btn-primary">Répondre</button>
              </form>
            </>
          )}
        </div>
      </Shell>
    </Guard>
  );
}

// =================================================== COVOITURAGE
export function EspaceCovoiturage() {
  const { espace, member } = useMember();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: "offer", from_city: "", to_city: "Vains", date: "", seats: 1, contact: "", notes: "" });
  const reload = () => espace.get("/espace/covoiturage").then(r => setItems(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  const submit = async (e) => {
    e.preventDefault();
    await espace.post("/espace/covoiturage", { ...form, seats: parseInt(form.seats) || 1 });
    setForm({ type: "offer", from_city: "", to_city: "Vains", date: "", seats: 1, contact: "", notes: "" });
    reload();
  };
  const remove = async (id) => { if (window.confirm("Supprimer ?")) { await espace.delete(`/espace/covoiturage/${id}`); reload(); } };
  return (
    <Guard>
      <Shell title="Covoiturage" kicker="TRAJETS PARTAGÉS">
        <div className="container-prieure max-w-4xl space-y-8">
          <form onSubmit={submit} data-testid="covoit-new" className="bg-white border p-6 grid grid-cols-1 sm:grid-cols-2 gap-3" style={{ borderColor: "var(--border)" }}>
            <h3 className="font-display text-xl sm:col-span-2">Proposer ou chercher un trajet</h3>
            <select value={form.type} onChange={(e)=>setForm(s=>({...s,type:e.target.value}))} className="input-classic">
              <option value="offer">Je propose un trajet</option>
              <option value="request">Je cherche un trajet</option>
            </select>
            <input type="date" required value={form.date} onChange={(e)=>setForm(s=>({...s,date:e.target.value}))} className="input-classic" />
            <input required placeholder="Départ (ville)" value={form.from_city} onChange={(e)=>setForm(s=>({...s,from_city:e.target.value}))} className="input-classic" />
            <input required placeholder="Arrivée (ville)" value={form.to_city} onChange={(e)=>setForm(s=>({...s,to_city:e.target.value}))} className="input-classic" />
            <input type="number" min="1" max="9" value={form.seats} onChange={(e)=>setForm(s=>({...s,seats:e.target.value}))} className="input-classic" placeholder="Places" />
            <input placeholder="Contact (tél / email)" value={form.contact} onChange={(e)=>setForm(s=>({...s,contact:e.target.value}))} className="input-classic" />
            <textarea rows={2} placeholder="Notes (horaire, point de RDV…)" value={form.notes} onChange={(e)=>setForm(s=>({...s,notes:e.target.value}))} className="input-classic sm:col-span-2" />
            <button className="btn btn-primary sm:col-span-2">Publier</button>
          </form>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="covoit-list">
            {items.length === 0 && <p className="text-center md:col-span-2" style={{ color: "var(--muted)" }}>Aucune annonce.</p>}
            {items.map(c => (
              <article key={c.id} className="bg-white border p-5" style={{ borderColor: "var(--border)", borderLeftWidth: 3, borderLeftColor: c.type === "offer" ? "var(--moss)" : "var(--lapis)" }}>
                <div className="flex justify-between items-start mb-1">
                  <span className="font-caps text-[0.65rem]" style={{ color: c.type === "offer" ? "var(--moss)" : "var(--lapis)", letterSpacing: "0.2em" }}>
                    {c.type === "offer" ? "PROPOSE" : "CHERCHE"}
                  </span>
                  {(member?.id === c.author_id || member?.role === "organisateur") && (
                    <button onClick={() => remove(c.id)} className="text-xs link-underline" style={{ color: "var(--cinnabar)" }}>Supprimer</button>
                  )}
                </div>
                <div className="font-display text-xl">{c.from_city} → {c.to_city}</div>
                <div className="text-sm mt-1" style={{ color: "var(--ink-2)" }}>
                  {new Date(c.date).toLocaleDateString("fr-FR")} · {c.seats} place(s)
                </div>
                {c.notes && <p className="text-sm mt-2">{c.notes}</p>}
                <div className="text-xs mt-3" style={{ color: "var(--muted)" }}>
                  {c.author_name}{c.contact ? ` · ${c.contact}` : ""}
                </div>
              </article>
            ))}
          </div>
        </div>
      </Shell>
    </Guard>
  );
}

// =================================================== GALERIE PHOTOS (file upload)
export function EspaceGalerie() {
  const { espace, member } = useMember();
  const [items, setItems] = useState([]);
  const [caption, setCaption] = useState("");
  const [file, setFile] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const reload = () => espace.get("/espace/photos").then(r => setItems(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);

  // Keyboard nav for the lightbox
  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(null);
      else if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % items.length);
      else if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + items.length) % items.length);
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIndex, items.length]);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { setMsg("Sélectionnez une image."); return; }
    setBusy(true); setMsg("");
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await espace.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await espace.post("/espace/photos", { image_url: up.data.url, caption });
      setCaption(""); setFile(null);
      const fi = document.querySelector('[data-testid="photo-file-input"]');
      if (fi) fi.value = "";
      reload();
    } catch (ex) { setMsg(formatErr(ex)); } finally { setBusy(false); }
  };
  const remove = async (id) => { if (window.confirm("Supprimer ?")) { await espace.delete(`/espace/photos/${id}`); reload(); } };

  const srcOf = (p) => p.image_url?.startsWith("/uploads") ? `${process.env.REACT_APP_BACKEND_URL}${p.image_url}` : p.image_url;

  return (
    <Guard>
      <Shell title="Galerie photos" kicker="MÉMOIRE DES FÊTES">
        <div className="container-prieure max-w-5xl space-y-8">
          <form onSubmit={submit} data-testid="photo-new" className="bg-white border p-6 space-y-3" style={{ borderColor: "var(--border)" }}>
            <h3 className="font-display text-xl">Partager une photo</h3>
            <input type="file" accept="image/*" data-testid="photo-file-input" onChange={(e)=>setFile(e.target.files?.[0] || null)} className="block w-full text-sm" />
            <input placeholder="Légende (optionnelle)" value={caption} onChange={(e)=>setCaption(e.target.value)} className="input-classic" />
            {msg && <p className="text-sm" style={{ color: msg.startsWith("✓") ? "var(--moss)" : "var(--cinnabar)" }}>{msg}</p>}
            <button disabled={busy} className="btn btn-primary">{busy ? "Envoi…" : "Publier"}</button>
            <p className="text-xs" style={{ color: "var(--muted)" }}>JPG/PNG/WebP/GIF · 20 Mo max</p>
          </form>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="photo-grid">
            {items.length === 0 && <p className="text-center col-span-full" style={{ color: "var(--muted)" }}>Aucune photo pour l'instant.</p>}
            {items.map((p, idx) => {
              const src = srcOf(p);
              return (
                <figure key={p.id} className="relative bg-white border group" style={{ borderColor: "var(--border)" }}>
                  <button type="button" onClick={() => setLightboxIndex(idx)}
                          data-testid={`photo-open-${p.id}`}
                          className="block w-full focus:outline-none"
                          aria-label="Ouvrir en grand">
                    <img src={src} alt={p.caption || ""} loading="lazy"
                         className="w-full h-48 object-cover transition-transform group-hover:scale-105"
                         onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                  </button>
                  <figcaption className="p-3">
                    {p.caption && <div className="text-sm">{p.caption}</div>}
                    <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>{p.author_name}</div>
                  </figcaption>
                  {(member?.id === p.author_id || member?.role === "organisateur") && (
                    <button onClick={() => remove(p.id)} className="absolute top-1 right-1 text-xs bg-white/80 px-2 py-1" style={{ color: "var(--cinnabar)" }}>✕</button>
                  )}
                </figure>
              );
            })}
          </div>
        </div>

        {/* Fullscreen lightbox */}
        {lightboxIndex !== null && items[lightboxIndex] && (
          <PhotoLightbox
            items={items}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onPrev={() => setLightboxIndex((i) => (i - 1 + items.length) % items.length)}
            onNext={() => setLightboxIndex((i) => (i + 1) % items.length)}
            srcOf={srcOf}
          />
        )}
      </Shell>
    </Guard>
  );
}

function PhotoLightbox({ items, index, onClose, onPrev, onNext, srcOf }) {
  const p = items[index];
  if (!p) return null;
  return (
    <div data-testid="photo-lightbox"
         role="dialog" aria-modal="true"
         onClick={onClose}
         style={{
           position: "fixed", inset: 0, zIndex: 100,
           background: "rgba(8,6,4,0.96)",
           display: "flex", alignItems: "center", justifyContent: "center",
           padding: "2rem",
         }}>
      {/* Close button */}
      <button type="button" onClick={(e) => { e.stopPropagation(); onClose(); }}
              data-testid="photo-lightbox-close"
              aria-label="Fermer"
              style={{
                position: "absolute", top: "1.5rem", right: "1.5rem",
                background: "rgba(255,255,255,0.1)", color: "white",
                border: "1px solid rgba(255,255,255,0.3)",
                width: 44, height: 44, borderRadius: "50%",
                fontSize: "1.4rem", cursor: "pointer",
                backdropFilter: "blur(8px)",
              }}>×</button>

      {/* Prev */}
      {items.length > 1 && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onPrev(); }}
                data-testid="photo-lightbox-prev"
                aria-label="Précédente"
                style={{
                  position: "absolute", left: "1.5rem", top: "50%", transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.1)", color: "white",
                  border: "1px solid rgba(255,255,255,0.3)",
                  width: 48, height: 48, borderRadius: "50%",
                  fontSize: "1.6rem", cursor: "pointer",
                  backdropFilter: "blur(8px)",
                }}>‹</button>
      )}

      {/* Next */}
      {items.length > 1 && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onNext(); }}
                data-testid="photo-lightbox-next"
                aria-label="Suivante"
                style={{
                  position: "absolute", right: "1.5rem", top: "50%", transform: "translateY(-50%)",
                  background: "rgba(255,255,255,0.1)", color: "white",
                  border: "1px solid rgba(255,255,255,0.3)",
                  width: 48, height: 48, borderRadius: "50%",
                  fontSize: "1.6rem", cursor: "pointer",
                  backdropFilter: "blur(8px)",
                }}>›</button>
      )}

      {/* Image container */}
      <div onClick={(e) => e.stopPropagation()}
           style={{ maxWidth: "min(1400px, 92vw)", maxHeight: "92vh", display: "flex", flexDirection: "column", alignItems: "center", gap: "1rem" }}>
        <img src={srcOf(p)} alt={p.caption || ""}
             style={{ maxWidth: "100%", maxHeight: "78vh", objectFit: "contain",
                       boxShadow: "0 20px 60px rgba(0,0,0,0.5)" }} />
        {(p.caption || p.author_name) && (
          <figcaption className="text-center" style={{ color: "rgba(255,255,255,0.92)", maxWidth: "60ch" }}>
            {p.caption && <div className="font-display italic text-lg">{p.caption}</div>}
            {p.author_name && (
              <div className="font-caps text-[0.65rem] mt-2" style={{ letterSpacing: "0.22em", color: "rgba(255,255,255,0.55)" }}>
                {p.author_name}
              </div>
            )}
          </figcaption>
        )}
        <div className="font-caps text-[0.6rem]" style={{ letterSpacing: "0.25em", color: "rgba(255,255,255,0.4)" }}>
          {index + 1} / {items.length}
        </div>
      </div>
    </div>
  );
}

// =================================================== DISPONIBILITÉS BÉNÉVOLES
export function EspaceDisponibilites() {
  const { espace, member } = useMember();
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: "", start_time: "09:00", end_time: "17:00", notes: "" });
  const reload = () => espace.get("/espace/disponibilites").then(r => setItems(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, []);
  const submit = async (e) => {
    e.preventDefault();
    await espace.post("/espace/disponibilites", form);
    setForm({ date: "", start_time: "09:00", end_time: "17:00", notes: "" }); reload();
  };
  const remove = async (id) => { if (window.confirm("Supprimer ?")) { await espace.delete(`/espace/disponibilites/${id}`); reload(); } };
  const isOrg = member?.role === "organisateur";
  return (
    <Guard allow={["benevole", "organisateur"]}>
      <Shell title="Disponibilités" kicker={isOrg ? "TOUTES LES DISPONIBILITÉS" : "MES CRÉNEAUX BÉNÉVOLE"}>
        <div className="container-prieure max-w-3xl space-y-8">
          {!isOrg && (
            <form onSubmit={submit} data-testid="dispo-new" className="bg-white border p-6 grid grid-cols-1 sm:grid-cols-3 gap-3" style={{ borderColor: "var(--border)" }}>
              <h3 className="font-display text-xl sm:col-span-3">Ajouter un créneau</h3>
              <label className="block"><span className="label-caps">DATE</span><input required type="date" value={form.date} onChange={(e)=>setForm(s=>({...s,date:e.target.value}))} className="input-classic mt-1" /></label>
              <label className="block"><span className="label-caps">DÉBUT</span><input required type="time" value={form.start_time} onChange={(e)=>setForm(s=>({...s,start_time:e.target.value}))} className="input-classic mt-1" /></label>
              <label className="block"><span className="label-caps">FIN</span><input required type="time" value={form.end_time} onChange={(e)=>setForm(s=>({...s,end_time:e.target.value}))} className="input-classic mt-1" /></label>
              <textarea rows={2} placeholder="Notes (compétences, préférences…)" value={form.notes} onChange={(e)=>setForm(s=>({...s,notes:e.target.value}))} className="input-classic sm:col-span-3" />
              <button className="btn btn-primary sm:col-span-3">Enregistrer</button>
            </form>
          )}
          <div className="space-y-2" data-testid="dispo-list">
            {items.length === 0 && <p className="text-center" style={{ color: "var(--muted)" }}>Aucun créneau.</p>}
            {items.map(d => (
              <article key={d.id} className="bg-white border p-4 flex justify-between items-center" style={{ borderColor: "var(--border)", borderLeftWidth: 3, borderLeftColor: "var(--gold)" }}>
                <div>
                  <div className="font-display text-lg">{new Date(d.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</div>
                  <div className="text-sm" style={{ color: "var(--ink-2)" }}>{d.start_time} → {d.end_time}{isOrg ? ` · ${d.author_name}` : ""}</div>
                  {d.notes && <p className="text-sm mt-1">{d.notes}</p>}
                </div>
                {(member?.id === d.author_id || isOrg) && (
                  <button onClick={() => remove(d.id)} className="text-xs link-underline" style={{ color: "var(--cinnabar)" }}>Supprimer</button>
                )}
              </article>
            ))}
          </div>
        </div>
      </Shell>
    </Guard>
  );
}
